/**
 * Money as a person reads it, from the integer cents the ledger stores.
 *
 * One formatter, shared by everything that puts a figure in front of somebody —
 * a statement, an appeal, a screen. A bill and the reminder chasing it are about
 * the same debt, so they must not be able to spell it differently.
 */
export const formatMoney = (
  cents: number,
  options: {
    /**
     * "$200" rather than "$200.00", where a figure is read on its own rather
     * than down a column — a column keeps its cents so its figures line up.
     * A figure with cents keeps them either way.
     */
    dropZeroCents?: boolean
  } = {},
): string => {
  const whole = options.dropZeroCents && cents % 100 === 0
  /* A sum that comes to nothing by subtraction is -0, and -0 prints as
     "$-0.00"; nothing is nothing whichever way it was reached. */
  const value = cents === 0 ? 0 : cents
  return `$${(value / 100).toLocaleString('en-US', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`
}

/**
 * A balance as a person reads it, wherever it is printed: what is owed is the
 * amount, and money held for the billing account is the amount followed by
 * `CR`.
 * Never a minus — a bare minus is the accounting tools' own documented source
 * of confusion, and `CR` is how a bank or card statement names a credit.
 */
export const formatBalance = (
  cents: number,
  options: { dropZeroCents?: boolean } = {},
): string =>
  cents < 0 ? `${formatMoney(-cents, options)} CR` : formatMoney(cents, options)

/** How a stored payment method is written wherever a person reads it. */
export const formatPaymentMethod = (method: string): string =>
  method ? `${method[0]?.toUpperCase()}${method.slice(1)}` : ''

/**
 * A saved way to pay, as the processor holds it: a card, or a card held in
 * Link. Link gives its card's brand and last four where it gives them, and
 * otherwise only that it is Link. Read as it stands, never stored.
 */
export type SavedCard = {
  /** Saved through Link rather than typed as a card. */
  link?: boolean
  brand: string | null
  last4: string | null
  expMonth: number | null
  expYear: number | null
}

/** The card's name: "Visa ending 4242", "Link · Visa ending 4242", or "Link". */
export const formatCardName = (card: SavedCard): string => {
  const typed =
    card.brand && card.last4
      ? `${formatPaymentMethod(card.brand)} ending ${card.last4}`
      : null
  if (card.link) return typed ? `Link · ${typed}` : 'Link'
  return typed ?? 'A saved card'
}

/** When the card expires — "expires 04/2030" — or null where it was not given. */
export const formatCardExpiry = (card: SavedCard): string | null =>
  card.expMonth && card.expYear
    ? `expires ${String(card.expMonth).padStart(2, '0')}/${card.expYear}`
    : null

/**
 * A saved card as a person recognizes it — its brand, its last four and when
 * it expires — in the words every screen that names a card uses.
 */
export const formatCard = (card: SavedCard): string =>
  [formatCardName(card), formatCardExpiry(card)].filter(Boolean).join(', ')

/**
 * The same figure in whole dollars — "$50,000".
 *
 * For a total being read rather than reconciled: a campaign's goal and what it
 * has raised are read at a glance and compared with each other, and cents on a
 * five-figure number are two characters that carry no information and cost the
 * eye the thousands separator. Anywhere the number must ADD UP against another
 * — a statement, an invoice, a payment — keeps `formatMoney`, because a rounded
 * cent that cannot be reconciled is worse than a long number.
 *
 * The direction is the caller's to state, because both errors are one-sided and
 * they point opposite ways. A figure that has been RAISED rounds down, so it can
 * never claim money that is not in; a TARGET rounds up, so it can never shrink
 * to meet what has been raised. Rounding to nearest gets both wrong in the same
 * place: $49,999.60 of a $50,000.99 goal reads "$50,000 raised of $50,000" and
 * announces a drive as finished while it is short at both ends.
 */
export const formatMoneyWhole = (
  cents: number,
  toward: 'down' | 'up' = 'down',
): string =>
  `$${(toward === 'up' ? Math.ceil(cents / 100) : Math.floor(cents / 100)).toLocaleString('en-US')}`

/**
 * The cents behind what somebody typed into a dollar field, or `null` where
 * that is not a number.
 *
 * Null rather than `0` or `NaN`, because those are both amounts a caller can
 * fail to notice: `0` is a figure the form will happily submit, and `NaN`
 * survives arithmetic to reach a screen. Null makes the empty field and the
 * typo the same answer, which is the same thing every caller has to check.
 */
export function centsFrom(typed: string): number | null {
  const text = typed.trim()
  if (!text) return null
  const dollars = Number(text)
  return Number.isFinite(dollars) ? Math.round(dollars * 100) : null
}

/**
 * Cents as somebody would type them into a dollar field: whole dollars bare,
 * anything else to the cent, so $290.90 reads `290.90` and never `290.9`.
 */
export function typedFrom(cents: number): string {
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2)
}

/**
 * A sum of cents divided in proportion to the given shares, the pieces adding
 * back to the sum exactly.
 *
 * One payment that gave to several campaigns has one card fee, one covered
 * surcharge and one refund, and each campaign's gift carries only its part of
 * each. Every piece is rounded down and the cents left over go to the largest
 * remainders, earlier shares first on a tie — so a cent is never invented or
 * lost, and the same split comes out every time it is asked.
 */
export function apportion(totalCents: number, shares: number[]): number[] {
  const weight = shares.reduce((sum, s) => sum + Math.max(s, 0), 0)
  if (shares.length === 0) return []
  if (weight === 0) return shares.map((_, i) => (i === 0 ? totalCents : 0))
  const exact = shares.map((s) => (totalCents * Math.max(s, 0)) / weight)
  const pieces = exact.map(Math.floor)
  let left = totalCents - pieces.reduce((sum, p) => sum + p, 0)
  const order = exact
    .map((x, i) => ({ i, rest: x - Math.floor(x) }))
    .sort((a, b) => b.rest - a.rest || a.i - b.i)
  for (const { i } of order) {
    if (left <= 0) break
    pieces[i] = (pieces[i] ?? 0) + 1
    left -= 1
  }
  return pieces
}
